function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import produce from 'immer';
import React from 'react';
import { ControlBarAction, ControlBarActionItems } from 'common/query-builder/query-entry-section/constants';
import { DraggableBehavior, useBehaviorsList } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/behaviors-list';
import type { RowProps } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { getEmptyEventMetricEntryBehavior } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/utils';
import { MetricEntryBehavior } from 'common/query-builder/query-entry-section/types';
import { getAlphaLabelFromIndex } from 'common/util';
import { useTranslation } from 'common/i18n/hooks';
import { useFunnelAnyOrderIconToggle } from '../util';
export function EventComparisonRow(props: RowProps & {
  metricIndex: number;
  parentMetricIndex?: number;
  disableAnyOrderMessage?: string;
  index: number;
  onUpdate: RowProps[`updateEntry`];
}) {
  const {
    t
  } = useTranslation();
  const isStagingEventComparison = React.useRef(false);
  const {
    behaviors,
    handleUpdateBehavior,
    handleDuplicateBehavior,
    handleAddBehavior
  } = useBehaviorsList(props.entry, props.onUpdate);
  const {
    rowIcon,
    toggleAnyOrder
  } = useFunnelAnyOrderIconToggle(props.entry, props.index, props.updateEntry);
  const handleAddEventComparison = () => {
    isStagingEventComparison.current = true;
    handleAddBehavior(getEmptyEventMetricEntryBehavior(), {
      requery: false
    });
  };
  const handleUpdateEventComparison = (index: number, behavior: MetricEntryBehavior) => {
    isStagingEventComparison.current = false;
    handleUpdateBehavior(index, behavior);
  };

  /**
   * Handles the deletion of an event-comparison behavior at the specified index.
   *
   * If removing the given event-comparison behavior leaves the parent behavior
   * with only one nested behavior left, we collapse this nested behaviors and
   * turn the parent behavior into a last remaining nested behavior.
   *
   * E.g. If you have behavior: { behaviors: [A,B] } and delete B, the behavior
   * will be updated to behavior: { B }.
   *
   * @param index Index of the behavior to update.
   */
  const handleDeleteEventComparison = function handleDeleteEventComparison(index: number) {
    let opts: {
      requery?: boolean;
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const updatedEntry = produce(props.entry, entryDraft => {
      if (entryDraft.behavior.behaviors) {
        // Remove the event comparison event at the specified index.
        entryDraft.behavior.behaviors.splice(index, 1);

        // If we only have one nested behavior left, we should flatten it.
        if (entryDraft.behavior.behaviors.length === 1) {
          entryDraft.behavior = _objectSpread({}, entryDraft.behavior.behaviors[0]);
        }
      }
      return entryDraft;
    });
    props.onUpdate(updatedEntry, {
      requery: opts === null || opts === void 0 ? void 0 : opts.requery
    });
  };
  const handleCloseEventComparison = (index: number) => {
    // If the qb is closed for an empty, staged event comparison we should
    // remove it.
    if (isStagingEventComparison.current && !behaviors[index].name) {
      isStagingEventComparison.current = false;
      handleDeleteEventComparison(index, {
        requery: false
      });
    }
  };
  return <div data-sentry-component="EventComparisonRow" data-sentry-source-file="index.tsx">
      {behaviors.map((subBehavior, index) => {
      var _subBehavior$name, _behaviors$at;
      return <DraggableBehavior {...props} index={index} indexOverride={props.index}
      /** prevents drag interactions inside step comparisons */ disableDrag={index > 0} metricIndex={props.metricIndex} parentMetricIndex={props.parentMetricIndex}
      // props.index is the index of the behavior index in the parent metric
      behaviorIndex={[props.index, index]}
      /** prevents drag interactions between step comparisons */ disableSortZones={index !== 0} isLast={index === behaviors.length - 1} additionalOverflowControls={[_objectSpread(_objectSpread({}, (ControlBarActionItems as any)[ControlBarAction.AddGroupedEvent]), {}, {
        onSelect: () => handleAddEventComparison()
      }), _objectSpread(_objectSpread({}, (ControlBarActionItems as any)[ControlBarAction.ToggleAnyOrder]), {}, {
        onSelect: () => toggleAnyOrder()
      }, props.disableAnyOrderMessage ? {
        isDisabled: true,
        tooltip: {
          text: props.disableAnyOrderMessage
        }
      } : {})]} behavior={_objectSpread(_objectSpread({}, subBehavior), {}, {
        name: (_subBehavior$name = subBehavior.name) !== null && _subBehavior$name !== void 0 ? _subBehavior$name : t("query-builder.metrics.add-step")
      })}
      /** prevents behavioral or computed filters from showing up */ otherFilterOptions={[]}
      // @todo(metrics) Figure out a way to uniquely identify each behavior in a metric
      // key={index}
      iconLabel={rowIcon ? null : String(props.index + 1)} onClose={() => handleCloseEventComparison(index)} onDelete={() => handleDeleteEventComparison(index)} onDuplicate={() => handleDuplicateBehavior(index)} onSelect={behavior => handleUpdateEventComparison(index, behavior)}
      // only open  last staged event
      openQueryBlock={index === behaviors.length - 1 && !((_behaviors$at = behaviors.at(-1)) !== null && _behaviors$at !== void 0 && _behaviors$at.name)} prefixLabel={getAlphaLabelFromIndex(index) + " "} rowIcon={rowIcon} suffixLabel={behaviors.length - 1 === index ? " then" : " or"} />;
    })}
    </div>;
}